<template>
    <div>
        <template v-if="isAdmin">
            <AdminYearFilter
                title="Employee Leave Credits"
                :all-employees="employeeList"
                @user-changed="handleAdminUserChanged"
                @date-change="handleDateChange"
            >
                <template #actions>
                    <Button class="pt-btn-3" @click="openModal">
                        <Icon type="md-add" size="18" /> Grant Leave Credit
                    </Button>
                </template>
            </AdminYearFilter>

            <List
                :total-count="totalCount"
                :current-page="currentPage"
                :limit="limit"
                :requests-data-list="tblData"
                :is-admin="isAdmin"
                @on-page-change="handleOnPageChanged"
                @reload="handleReload"
            />
        </template>

        <template v-else>
            <RequestYearToolbar
                title="My Leave Credits"
                button-label="New Leave Credit Request"
                :year="selectedYear"
                @year-changed="handleYearChanged"
                @create="openModal"
            />
            <EmployeeCreditList
                :is-loading="isLoading"
                :total-count="totalCount"
                :current-page="currentPage"
                :limit="employeeLimit"
                :requests-data-list="tblData"
                @on-page-change="handleEmployeePageChanged"
                @reload="handleReload"
            />
        </template>

        <CreateModal ref="CreateRequest" :is-admin="isAdmin" @create="handleReload"></CreateModal>
    </div>
</template>

<script>
import CreateModal from '@/components/Requests/Dev/Leave/LeaveCredit/create.vue';
import AdminYearFilter from '@/components/Requests/Admin/AdminYearFilter.vue';
import { getUsers } from '@/services/users.services';
import List from '@/components/Requests/Dev/Leave/LeaveCredit/list.vue';
import EmployeeCreditList from '@/components/Requests/Dev/Leave/LeaveCredit/EmployeeCreditList.vue';
import RequestYearToolbar from '@/components/Requests/Dev/shared/RequestYearToolbar.vue';
import moment from 'moment';
import { endOfDate, startOfDate } from '../../../../../../utility/helper';
import {
    adminGetSubmittedLeaveCreditList,
    getSubmittedLeaveCreditList,
} from '@/components/Requests/Dev/Leave/LeaveCredit/services/credit.services';
export default {
    components: {
        AdminYearFilter,
        List,
        EmployeeCreditList,
        RequestYearToolbar,
        CreateModal,
    },
    props: {
        isAdmin: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedStatus: this.$route.query.status,
            selectedUser: localStorage.getItem('user_id'),
            currentPage: 1,
            limit: 2,
            totalCount: 0,
            totalPages: 0,
            filter: {},
            tblData: [],
            selectedDates: null,
            employeeList: [],
            selectedEmployee: null,
            // Employee view only
            isLoading: false,
            selectedYear: moment().year(),
            employeeLimit: 50,
        };
    },
    created() {
        if (this.$route.query.status) {
            this.$emit('status-changed', this.selectedStatus);
        }
    },
    mounted() {
        if (!this.isAdmin) {
            this.fetchEmployeeCredits();
            return;
        }
        // AdminYearFilter emits the current year on mount, which triggers the first fetch
        this.handleGetAllEmployees();
    },
    methods: {
        handleReload() {
            this.$emit('reload');
            if (!this.isAdmin) {
                this.fetchEmployeeCredits();
                return;
            }
            this.getList({ page: this.currentPage, limit: this.limit, filter: this.filter });
        },
        handleGetAllEmployees() {
            getUsers()
                .then((res) => {
                    const employees = (res.data || []).filter(
                        (x) => x.mainRole !== 'client' && x.mainRole !== 'admin' && x.isArchived === false
                    );
                    this.employeeList = employees.map((i) => ({
                        value: i._id,
                        label: i.name,
                        name: i.name,
                        key: i._id,
                        _id: i._id,
                    }));
                })
                .catch(() => {
                    this.$Message.error('Failed to get users');
                });
        },
        handleAdminUserChanged(userId) {
            this.handleUserSelected(userId ? { _id: userId } : null);
        },
        openModal() {
            this.$refs.CreateRequest.openModal({ userId: this.selectedUser }, false);
        },
        handleUserSelected(user) {
            console.log('handleUserSelected: ', user);
            this.filter = {
                ...(user && { user: user._id }),
                ...(this.filter.status && { status: this.filter.status }),
                ...(this.filter.createdAt && { createdAt: this.filter.createdAt }),
            };
            this.currentPage = 1;
            this.getList({ page: this.currentPage, limit: this.limit, filter: this.filter });
        },

        handleStatusChanged(stats) {
            this.filter = {
                status: stats,
                ...(!this.isAdmin && { user: this.selectedUser }),
                ...(this.isAdmin && this.filter.user && { user: this.filter.user }),
                ...(this.filter.createdAt && { createdAt: this.filter.createdAt }),
            };
            this.currentPage = 1;
            this.getList({ page: this.currentPage, limit: this.limit, filter: this.filter });
        },
        handleDateChange(date) {
            this.filter = {
                ...(!this.isAdmin && { user: this.selectedUser }),
                ...(this.filter.status && { status: this.filter.status }),
                ...(this.isAdmin && this.filter.user && { user: this.filter.user }),
                ...(date.from && { createdAt: { $gte: date.from, $lte: date.to } }),
            };
            this.currentPage = 1;
            this.getList({ page: this.currentPage, limit: this.limit, filter: this.filter });
        },
        handleOnPageChanged(page) {
            this.currentPage = page;
            // Keep every active filter (employee + year) when paging
            this.getList({ page, filter: this.filter, limit: this.limit });
        },
        handleYearChanged(year) {
            this.selectedYear = year;
            this.currentPage = 1;
            this.fetchEmployeeCredits();
        },
        handleEmployeePageChanged(page) {
            this.currentPage = page;
            this.fetchEmployeeCredits();
        },
        async fetchEmployeeCredits() {
            const yearStart = moment({ year: this.selectedYear, month: 0, date: 1 });
            this.isLoading = true;
            try {
                const { data } = await getSubmittedLeaveCreditList({
                    page: this.currentPage,
                    limit: this.employeeLimit,
                    filter: {
                        createdAt: {
                            $gte: startOfDate(yearStart),
                            $lte: endOfDate(moment(yearStart).endOf('year')),
                        },
                    },
                });
                this.tblData = data.data || [];
                this.totalCount = data.totalCount || 0;
            } catch (err) {
                this.$Message.error('Failed to get leave credit requests');
                this.tblData = [];
                this.totalCount = 0;
            } finally {
                this.isLoading = false;
            }
        },
        async getList({ page = 1, limit = 1, filter = {} }) {
            try {
                if (!this.isAdmin) {
                    const { data } = await getSubmittedLeaveCreditList({
                        page,
                        limit,
                        filter: { ...filter, ...(!this.isAdmin && { user: this.selectedUser }) },
                    });

                    this.tblData = data.data;
                    this.totalCount = data.totalCount;
                } else {
                    const { data } = await adminGetSubmittedLeaveCreditList({
                        page,
                        limit,
                        filter: { ...filter, ...(!this.isAdmin && { user: this.selectedUser }) },
                    });

                    this.tblData = data.data;
                    this.totalCount = data.totalCount;
                }
            } catch (err) {
                console.log('trying to get list leave credit: ', err);
            }
        },
    },
};
</script>
<style lang="scss">
@media screen and (max-width: 767px) {
    .requests-filter {
        .ivu-row {
            display: block;

            .ivu-col {
                max-width: 100%;
            }
            .ivu-col-span-xs-2 {
                margin: 5px 0;
            }
            .filter-btn button {
                width: 100%;
            }
        }
    }
}
</style>
